/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import error from '../../../../common/nodejs/errors';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED } from '../../../../share/util/api/polling';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  FAILED: 'failed',
  SUCCESS: 'success',
  NOT_SUPPORTED_ERROR: 'not_supported_error'
};

export default {
  name: 'requirement40004',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      systemId: '',
      modal_requirement_40004: false,
      displayInfo: '',
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
        case PHASE.NOT_SUPPORTED_ERROR:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.START:
        default:
          return 'secondary';
      }
    },

    headerTitle() {
      // 待機
      const ExecutionTitle = 'individual.common_title_execution';
      // 正常終了
      const SuccessTitle = 'message.common_title_success';
      // 非サポートエラー, 共通エラー
      const ErrorTitle = 'message.common_title_error';
      const titleMap = {
        start: ExecutionTitle,
        success: SuccessTitle,
        not_supported_error: ErrorTitle,
        failed: ErrorTitle
      };
      return this.$t(titleMap[this.phase]);
    },

    workSupportMessage() {
      const messageMap = {
        start: `${this.i18nWsKey}.REQ_WS_40004_TXT_1`,
        success: this.displayInfo,
        not_supported_error: `${this.i18nWsKey}.REQ_WS_40004_TXT_2`,
        failed: `individual.error_communication_error`
      };
      return this.phase === PHASE.SUCCESS ? messageMap[this.phase] : this.$t(messageMap[this.phase]);
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      this.modal_requirement_40004 = true;
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_40004 = false;
    },

    /** ECU品番読み出し処理 */
    async execute() {
      try {
        this.isLoading = true;
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          // POST リクエストの body を指定する
          {
            id_token: token.id_token,
            type: 'read'
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case REQUEST_STATUS_COMPLETED:
              // 正常終了：読み出しデータ表示
              this.displayInfo = response.data.user_data.result;
              this.phase = PHASE.SUCCESS;
              break;
            case REQUEST_STATUS_FAILED:
              // 異常終了
              switch (response.data.async_code) {
                case error.WSP_40004_NOT_SUPPORTED_ERROR:
                  // ECU非サポートエラー
                  this.phase = PHASE.NOT_SUPPORTED_ERROR;
                  break;
                default:
                  // 共通エラーに遷移
                  this.showUnexpectedError();
                  break;
              }
              break;
            default:
              // 共通エラーに遷移
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 異常終了：エラー表示
      this.phase = PHASE.FAILED;
    },

    /**
      * API 通信で共通バリデーションでエラーが発生した際のコールバック
      * @param {object} result バリデーション結果
      */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** OKボタン押下時の処理 */
    onOkClicked() {
      this.closeWizard();
    },
  },

  async mounted() {
    this.init();
    // ECU品番読み出し処理
    await this.execute();
  },
};